import React, { useState, useEffect, useCallback } from "react";

const BASE_API_URL = process.env.REACT_APP_API_URL || "https://api.virtualkalvi.com";
const API_KEY      = process.env.REACT_APP_API_KEY  || "";
const H = { "Content-Type": "application/json", "X-API-Key": API_KEY };

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  page:   { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a3a2a)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"20px 14px 90px" },
  card:   { background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px 20px", marginBottom:18, backdropFilter:"blur(8px)" },
  title:  { fontSize:22, fontWeight:800, color:"#fff", marginBottom:4 },
  sub:    { fontSize:13, color:"#94a3b8", marginBottom:20 },
  label:  { display:"block", fontSize:11, fontWeight:700, color:"#94a3b8", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.5px" },
  input:  { width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:9, color:"#fff", padding:"9px 13px", fontSize:14, outline:"none", boxSizing:"border-box", marginBottom:12 },
  select: { width:"100%", background:"#1a3a2a", border:"1px solid rgba(255,255,255,0.15)", borderRadius:9, color:"#fff", padding:"9px 13px", fontSize:14, outline:"none", boxSizing:"border-box", marginBottom:12 },
  row:    { display:"flex", gap:9, flexWrap:"wrap", marginTop:6 },
  btn:    (c) => ({ background:c, color:"#fff", border:"none", borderRadius:9, padding:"9px 18px", fontWeight:700, fontSize:13, cursor:"pointer" }),
  sectionTitle: { fontSize:15, fontWeight:700, color:"#fff", marginBottom:12, display:"flex", alignItems:"center", gap:8 },
  statusDot: (on) => ({ display:"inline-block", width:10, height:10, borderRadius:"50%", background:on?"#22c55e":"#ef4444", marginRight:6, boxShadow:on?"0 0 6px #22c55e":"none" }),
  logRow: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.06)", fontSize:13 },
  toast:  (t) => ({ position:"fixed", bottom:20, left:"50%", transform:"translateX(-50%)", background:t==="error"?"#dc2626":"#16a34a", color:"#fff", borderRadius:11, padding:"9px 20px", fontWeight:600, fontSize:13, zIndex:9999, boxShadow:"0 4px 20px rgba(0,0,0,0.4)" }),
  chip:   { background:"rgba(255,255,255,0.1)", borderRadius:20, padding:"3px 11px", fontSize:12, display:"inline-flex", alignItems:"center", gap:5, cursor:"pointer" },
  tag:    (c) => ({ background:c, color:"#fff", borderRadius:20, padding:"2px 9px", fontSize:11, fontWeight:700 }),
};

const SEND_TIME_OPTIONS = ["06:00","07:00","08:00","09:00","10:00","12:00","14:00","16:00","18:00","20:00","21:00","22:00"];
const SUBJECT_OPTIONS   = ["Science","Maths","Social Science","Tamil","English","Physics","Chemistry","Biology","History","Geography","Computer Science","Economics"];
const STANDARD_OPTIONS  = ["6","7","8","9","10","11","12"];

export default function WASchedulerPage() {
  const [info,      setInfo]      = useState(null);
  const [students,  setStudents]  = useState([]);
  const [logs,      setLogs]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [toast,     setToast]     = useState(null);
  const [tab,       setTab]       = useState("scheduler"); // scheduler | students | logs

  // Scheduler config form
  const [enabled,    setEnabled]    = useState(false);
  const [sendTimes,  setSendTimes]  = useState(["08:00","14:00","20:00"]);
  const [language,   setLanguage]   = useState("both");

  // Add student form
  const [newName,     setNewName]     = useState("");
  const [newMobile,   setNewMobile]   = useState("");
  const [newSubjects, setNewSubjects] = useState([{ standard:"10", subject:"Science" }]);

  const showToast = (msg, type="success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchAll = useCallback(async () => {
    try {
      const [infoRes, stuRes, logRes] = await Promise.all([
        fetch(`${BASE_API_URL}/wascheduler`, { headers: H }),
        fetch(`${BASE_API_URL}/wastudents`,  { headers: H }),
        fetch(`${BASE_API_URL}/wascheduler/logs`, { headers: H }),
      ]);
      const infoData = await infoRes.json();
      const stuData  = await stuRes.json();
      const logData  = await logRes.json();

      setInfo(infoData);
      setEnabled(infoData.config?.enabled || false);
      setSendTimes(infoData.config?.send_times || ["08:00","14:00","20:00"]);
      setLanguage(infoData.config?.language || "both");
      setStudents(stuData || []);
      setLogs(logData || []);
    } catch (e) {
      showToast("Load error: " + e.message, "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Scheduler save
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BASE_API_URL}/wascheduler`, {
        method:"POST", headers:H,
        body: JSON.stringify({ enabled, send_times: sendTimes, language }),
      });
      if (!res.ok) throw new Error("Save failed");
      showToast(enabled ? "✅ Scheduler saved & running!" : "✅ Scheduler saved (disabled)");
      fetchAll();
    } catch (e) { showToast("Error: "+e.message,"error"); }
    finally { setSaving(false); }
  };

  // ── Test send now
  const handleTestNow = async () => {
    const res = await fetch(`${BASE_API_URL}/wascheduler/testnow`, { method:"POST", headers:H });
    const d = await res.json();
    showToast("🚀 Sending now — " + (d.status || "started"));
    setTimeout(fetchAll, 4000);
  };

  // ── Toggle send time
  const toggleTime = (t) => {
    setSendTimes(prev =>
      prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].sort()
    );
  };

  // ── Add student subject row
  const addSubjectRow = () => setNewSubjects(p => [...p, { standard:"10", subject:"Science" }]);
  const removeSubjectRow = (i) => setNewSubjects(p => p.filter((_,j) => j!==i));
  const updateSubjectRow = (i, field, val) => setNewSubjects(p => p.map((r,j) => j===i ? {...r,[field]:val} : r));

  // ── Add student
  const handleAddStudent = async () => {
    if (!newName.trim() || !newMobile.trim()) { showToast("Name and mobile required","error"); return; }
    const res = await fetch(`${BASE_API_URL}/wastudents`, {
      method:"POST", headers:H,
      body: JSON.stringify({ name:newName.trim(), mobile:newMobile.trim(), subjects:newSubjects }),
    });
    if (!res.ok) { showToast("Add failed","error"); return; }
    showToast("✅ Student added!");
    setNewName(""); setNewMobile(""); setNewSubjects([{ standard:"10", subject:"Science" }]);
    fetchAll();
  };

  // ── Remove student
  const handleRemoveStudent = async (id, name) => {
    if (!window.confirm(`Remove ${name}?`)) return;
    await fetch(`${BASE_API_URL}/wastudents?id=${id}`, { method:"DELETE", headers:H });
    showToast("Student removed");
    fetchAll();
  };

  if (loading) return (
    <div style={{...S.page, display:"flex", alignItems:"center", justifyContent:"center"}}>
      <div style={{textAlign:"center"}}><div style={{fontSize:36,marginBottom:12}}>📱</div><div style={{color:"#94a3b8"}}>Loading…</div></div>
    </div>
  );

  const waConnected = info?.wa_status === "connected";

  return (
    <div style={S.page}>

      {/* Header */}
      <div style={{marginBottom:22}}>
        <div style={S.title}>📱 WhatsApp Question Scheduler</div>
        <div style={S.sub}>Send daily random questions to students via WhatsApp — free, using WhatsApp Web.</div>

        {/* WA Status pill */}
        <div style={{display:"inline-flex",alignItems:"center",background:"rgba(255,255,255,0.07)",borderRadius:20,padding:"5px 13px",fontSize:13,marginBottom:14}}>
          <span style={S.statusDot(waConnected)}/>
          {waConnected ? "WhatsApp Connected ✅" : "WhatsApp Not Connected ❌"}
          {!waConnected && <span style={{marginLeft:8,color:"#fbbf24",fontSize:11}}>→ Start Node.js service first</span>}
        </div>
      </div>

      {/* Node.js service setup box — show when not connected */}
      {!waConnected && (
        <div style={{...S.card, borderColor:"rgba(251,191,36,0.3)", background:"rgba(251,191,36,0.07)"}}>
          <div style={{...S.sectionTitle, color:"#fbbf24"}}>⚠️ WhatsApp Service Not Running</div>
          <div style={{fontSize:13,color:"#cbd5e1",lineHeight:1.9}}>
            <div>1. On your server, go to the <code style={{background:"rgba(255,255,255,0.1)",padding:"1px 6px",borderRadius:4}}>whatsapp-service</code> folder</div>
            <div>2. Run: <code style={{background:"rgba(0,0,0,0.4)",padding:"2px 8px",borderRadius:4,color:"#86efac"}}>npm install && node server.js</code></div>
            <div>3. A QR code will appear in the terminal</div>
            <div>4. Open WhatsApp on your phone → <strong style={{color:"#fff"}}>Linked Devices → Link a Device</strong> → Scan QR</div>
            <div>5. Done! Status above will turn green</div>
          </div>
          <button style={{...S.btn("#d97706"), marginTop:12}} onClick={fetchAll}>🔄 Check Again</button>
        </div>
      )}

      {/* Tabs */}
      <div style={{display:"flex",gap:8,marginBottom:18}}>
        {["scheduler","students","logs"].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            ...S.btn(tab===t ? "#16a34a" : "rgba(255,255,255,0.1)"),
            textTransform:"capitalize", padding:"8px 16px",
          }}>
            {t === "scheduler" ? "⏰ Schedule" : t === "students" ? "👥 Students" : "📋 History"}
          </button>
        ))}
        <button style={{...S.btn("rgba(255,255,255,0.06)"), marginLeft:"auto", fontSize:12}} onClick={fetchAll}>🔄</button>
      </div>

      {/* ── TAB: Scheduler ── */}
      {tab === "scheduler" && (
        <div style={S.card}>
          <div style={S.sectionTitle}>⏰ Schedule Settings</div>

          <label style={S.label}>Send times (tap to toggle)</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:8,marginBottom:16}}>
            {SEND_TIME_OPTIONS.map(t => (
              <div key={t} onClick={() => toggleTime(t)} style={{
                ...S.chip,
                background: sendTimes.includes(t) ? "#16a34a" : "rgba(255,255,255,0.09)",
                color: sendTimes.includes(t) ? "#fff" : "#94a3b8",
                fontWeight: sendTimes.includes(t) ? 700 : 400,
              }}>
                {t}
              </div>
            ))}
          </div>
          <div style={{fontSize:11,color:"#64748b",marginBottom:16}}>
            Selected: {sendTimes.length === 0 ? "None" : sendTimes.join(", ")}
          </div>

          <label style={S.label}>Question language</label>
          <select style={S.select} value={language} onChange={e => setLanguage(e.target.value)}>
            <option value="both">Tamil + English (both)</option>
            <option value="tamil">Tamil only</option>
            <option value="english">English only</option>
          </select>

          {/* Enable toggle */}
          <div style={{display:"flex",alignItems:"center",gap:14,background:"rgba(255,255,255,0.05)",borderRadius:9,padding:"11px 14px",marginBottom:14}}>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:14}}>Auto-send enabled</div>
              <div style={{fontSize:12,color:"#94a3b8",marginTop:2}}>
                Questions will be sent to all active students at the selected times
              </div>
            </div>
            <div onClick={() => setEnabled(!enabled)} style={{width:50,height:26,borderRadius:13,background:enabled?"#16a34a":"#475569",position:"relative",cursor:"pointer",transition:"background 0.2s"}}>
              <div style={{position:"absolute",top:3,left:enabled?26:3,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left 0.2s"}}/>
            </div>
          </div>

          <div style={S.row}>
            <button style={S.btn("#2563eb")} onClick={handleSave} disabled={saving}>
              {saving ? "Saving…" : "💾 Save & Apply"}
            </button>
            <button style={S.btn("#16a34a")} onClick={handleTestNow} disabled={!waConnected}>
              🚀 Send Now (Test)
            </button>
          </div>
          {!waConnected && <div style={{fontSize:11,color:"#ef4444",marginTop:8}}>Connect WhatsApp first to send</div>}

          {/* Stats */}
          <div style={{display:"flex",gap:12,marginTop:18,flexWrap:"wrap"}}>
            {[
              ["👥", "Students", students.filter(s=>s.active).length],
              ["📨", "Total Sent", logs.length],
              ["⏰", "Send Times", sendTimes.length + " per day"],
              ["🔌", "WA Status", waConnected?"Connected":"Offline"],
            ].map(([icon,label,val]) => (
              <div key={label} style={{flex:"1 1 120px",minWidth:100,background:"rgba(255,255,255,0.05)",borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
                <div style={{fontSize:22}}>{icon}</div>
                <div style={{fontSize:18,fontWeight:800,color:"#fff",marginTop:2}}>{val}</div>
                <div style={{fontSize:11,color:"#64748b"}}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TAB: Students ── */}
      {tab === "students" && (
        <>
          {/* Add student form */}
          <div style={S.card}>
            <div style={S.sectionTitle}>➕ Add Student</div>

            <label style={S.label}>Student Name</label>
            <input style={S.input} placeholder="e.g. பாவித்ரா" value={newName} onChange={e=>setNewName(e.target.value)}/>

            <label style={S.label}>Mobile Number (WhatsApp)</label>
            <input style={S.input} placeholder="e.g. 9876543210" value={newMobile} onChange={e=>setNewMobile(e.target.value)}/>

            <label style={S.label}>Subjects & Standard</label>
            {newSubjects.map((row, i) => (
              <div key={i} style={{display:"flex",gap:8,marginBottom:8,alignItems:"center"}}>
                <select style={{...S.select,marginBottom:0,flex:"0 0 80px"}} value={row.standard} onChange={e=>updateSubjectRow(i,"standard",e.target.value)}>
                  {STANDARD_OPTIONS.map(s => <option key={s} value={s}>Std {s}</option>)}
                </select>
                <select style={{...S.select,marginBottom:0,flex:1}} value={row.subject} onChange={e=>updateSubjectRow(i,"subject",e.target.value)}>
                  {SUBJECT_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {newSubjects.length > 1 && (
                  <button onClick={()=>removeSubjectRow(i)} style={{...S.btn("#dc2626"),padding:"6px 10px",fontSize:12}}>✕</button>
                )}
              </div>
            ))}
            <button onClick={addSubjectRow} style={{...S.btn("rgba(255,255,255,0.1)"),fontSize:12,marginBottom:14}}>
              + Add another subject
            </button>

            <div style={S.row}>
              <button style={S.btn("#16a34a")} onClick={handleAddStudent}>✅ Add Student</button>
            </div>
          </div>

          {/* Student list */}
          <div style={S.card}>
            <div style={S.sectionTitle}>👥 All Students ({students.length})</div>
            {students.length === 0 ? (
              <div style={{textAlign:"center",color:"#64748b",padding:"20px 0",fontSize:13}}>No students added yet.</div>
            ) : students.map(st => (
              <div key={st.id} style={{...S.logRow, flexDirection:"column", alignItems:"flex-start", paddingBottom:12, marginBottom:4}}>
                <div style={{display:"flex",width:"100%",justifyContent:"space-between",alignItems:"center"}}>
                  <div>
                    <span style={{fontWeight:700,fontSize:14}}>{st.name}</span>
                    <span style={{marginLeft:8,...S.tag(st.active?"#16a34a":"#64748b")}}>{st.active?"Active":"Inactive"}</span>
                  </div>
                  <button onClick={()=>handleRemoveStudent(st.id,st.name)} style={{...S.btn("#dc2626"),padding:"4px 10px",fontSize:11}}>Remove</button>
                </div>
                <div style={{fontSize:12,color:"#64748b",marginTop:3}}>📱 {st.mobile}</div>
                <div style={{display:"flex",flexWrap:"wrap",gap:5,marginTop:6}}>
                  {st.subjects.map((sub,i) => (
                    <span key={i} style={{...S.tag("#1e40af"),fontSize:11}}>Std {sub.standard} · {sub.subject}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ── TAB: Logs ── */}
      {tab === "logs" && (
        <div style={S.card}>
          <div style={S.sectionTitle}>📋 Send History ({logs.length})</div>
          {logs.length === 0 ? (
            <div style={{textAlign:"center",color:"#64748b",padding:"20px 0",fontSize:13}}>
              No messages sent yet. Use "Send Now (Test)" to try.
            </div>
          ) : logs.map((log,i) => (
            <div key={i} style={S.logRow}>
              <div>
                <span style={{fontWeight:600}}>{log.student_name}</span>
                <span style={{color:"#64748b",fontSize:11,marginLeft:8}}>{log.mobile}</span>
                <div style={{fontSize:12,color:"#94a3b8",marginTop:2}}>{log.subject_name} · {log.grade_name}</div>
              </div>
              <div style={{fontSize:11,color:"#64748b",textAlign:"right"}}>{log.sent_at}</div>
            </div>
          ))}
        </div>
      )}

      {toast && <div style={S.toast(toast.type)}>{toast.msg}</div>}
    </div>
  );
}
